/* eslint-disable react/no-redundant-should-component-update */
/**
 * This file is part of the "Awaken Media" project.
 *
 * (c) 2017 - CanalPlus International
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React from 'react';
import styled from 'styled-components';
import Slider from 'react-slick';
import isEqual from 'lodash/isEqual';
import partialRight from 'lodash/partialRight';
import { AutoAffix } from 'react-overlays';

import { COLORS, FONT_FAMILIES, FONT_WEIGHTS } from 'shared/constants/theme';
import { getZapNumber } from '../../helpers/getZapNumber';
import { GROUPING_MIN_PROGRAM_DURATION } from '../../constants';
import { HEADER_MOBILE_HEIGHT } from 'shared/constants/heights';
import BaseProgram from './../core/Program';
import Channels from './../core/Channels';
import mediaQueries from 'shared/helpers/styled-components/mediaQueries';
import ResponsiveHoc from 'shared/components/presentational/ResponsiveHOC';
import slidersConfig from './sliders.config';

const Program = styled(BaseProgram)`
  height: 130px;
  border: 1px solid ${props => props.theme.tvGuide.border};
  border-bottom: none;
  box-sizing: border-box;
  width: 100%;
`;

const Programs = styled.div`
  ${Program}:first-child {
    border-top: none;
  }
`;

const Headers = styled.div`
  background-color: #fff;
  z-index: 2;
  max-width: 1000px;
  margin: 0 auto;
  border-bottom: 1px solid ${props => props.theme.tvGuide.border};
`;

const HeadersWrapper = styled.div`
  position: relative;
  height: 40px;
  overflow-y: hidden;
`;

const Header = styled.div`
  text-align: center;
  font-size: 14px;
  font-family: ${FONT_FAMILIES.base};
  font-weight: ${FONT_WEIGHTS.bold};
  padding: 10px;
  height: 40px;

  ${mediaQueries.toPhablet`
    font-family: ${FONT_FAMILIES.brand};
  font-weight: ${FONT_WEIGHTS.demi};
    text-transform: uppercase;
  `};
`;

const Grid = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  // padding-top: 40px;
  position: relative;
  border-top: 1px solid ${props => props.theme.tvGuide.border};
  border-bottom: 1px solid ${props => props.theme.tvGuide.border};
  box-sizing: border-box;
  padding-left: 100px;
`;

const AbsoluteChannels = styled.div`
  position: absolute;
  left: 0;
  width: 100px;
`;

const Slide = styled.div``;

const Wrapper = styled.div`
  position: relative;

  .TvGuide__Slider--affix {
    height: 40px;
    z-index: 2;
    border-bottom: 1px solid ${COLORS.alto};
  }

  .slick-slider {
    top: -1px;
  }

  ${mediaQueries.fromDesktop`
    .slick-next,
    .slick-prev {
      display: none !important;
    }
  `};
`;

class Simple extends React.Component {
  displayName = 'TvGuide.Simple';

  onHeaderSliderChange = (oldIndex, newIndex) => {
    this.gridSlider.slickGoTo(newIndex);
  };
  onGridSliderChange = newIndex => {
    this.headerSlider.slickGoTo(newIndex);
  };

  shouldComponentUpdate(nextProps) {
    return !isEqual(this.props, nextProps);
  }

  render() {
    const {
      channels,
      headers,
      programs,
      fetchProgramsForChannelsIfNeeded,
      perPage,
      timezone,
      programUrlPattern,
      device,
      t,
      channelsApi: { isNew },
      viewportOffsetTop
    } = this.props;

    return (
      <Wrapper>
        <AutoAffix
          autoWidth
          viewportOffsetTop={viewportOffsetTop[device] || HEADER_MOBILE_HEIGHT}
          container={this}
          affixClassName="TvGuide__Slider--affix"
        >
          <HeadersWrapper>
            {/* div is needed: https://github.com/react-bootstrap/react-overlays/issues/121 */}
            <Headers>
              <Slider
                ref={c => (this.headerSlider = c)}
                {...slidersConfig.headers}
                beforeChange={this.onHeaderSliderChange}
              >
                {headers.map((header, index) => (
                  <Slide data-index={index} key={`TvGuidSimple.header${index}`}>
                    <Header>{t(header)}</Header>
                  </Slide>
                ))}
              </Slider>
            </Headers>
          </HeadersWrapper>
        </AutoAffix>

        <Grid>
          <AbsoluteChannels>
            <Channels
              channels={channels}
              fetch={partialRight(fetchProgramsForChannelsIfNeeded, 0, isNew)}
              perPage={perPage}
            />
          </AbsoluteChannels>

          <Slider
            ref={c => (this.gridSlider = c)}
            afterChange={this.onGridSliderChange}
            {...slidersConfig.grid}
          >
            <Slide data-index={1}>
              <Programs>
                {Object.values(channels).map((channel, index) => {
                  const zapNumber = getZapNumber(channel);

                  const data = programs[zapNumber];

                  const program = data && data.first;
                  return (
                    <Program
                      key={`TvGuidSimple.program.current${index}`}
                      programs={[program]}
                      channel={channel.LogoUrl}
                      timezone={timezone}
                      urlPattern={programUrlPattern}
                      duration={GROUPING_MIN_PROGRAM_DURATION}
                      isNew={isNew}
                    />
                  );
                })}
              </Programs>
            </Slide>
            <Slide data-index={2}>
              <Programs>
                {Object.values(channels).map((channel, index) => {
                  const zapNumber = getZapNumber(channel);

                  const data = programs[zapNumber];
                  const program = data && data.second;
                  return (
                    <Program
                      key={`TvGuidSimple.program.next${index}`}
                      programs={[program]}
                      channel={channel.LogoUrl}
                      timezone={timezone}
                      urlPattern={programUrlPattern}
                      duration={GROUPING_MIN_PROGRAM_DURATION}
                      isNew={isNew}
                    />
                  );
                })}
              </Programs>
            </Slide>
          </Slider>
        </Grid>
      </Wrapper>
    );
  }
}

export default ResponsiveHoc(Simple);
